/**
 * @file document-columns.tsx
 * @description Колонки реестра документов для `ResponsiveList` (phase19.31,
 * часть 3; SC46, SC43).
 *
 * Одно описание — две раскладки:
 *  - от 1024 таблица: Документ (вид + номер, второй линией контрагент) ·
 *    Дата · Основание (заказ или сделка ссылкой) · Сумма (справа, табличные
 *    цифры, «184 000,00 ₽») · Статус (пилюля по SC26). Строка в две линии,
 *    вся — ссылка на карточку; на 1024–1279 дата уходит второй линией под
 *    статус (`fold`), чтобы номер документа не обрезался;
 *  - до 1024 компактный список: «номер … сумма» / «вид · контрагент» /
 *    «статус · дата · основание». Внутри строки-ссылки ссылок нет —
 *    основание там текстом.
 *
 * Контрагент — с сервера у любого статуса (`counterpartyName`, контракт §19).
 * Незаполненные реквизиты продавца (`legalProfileIncomplete`) — янтарной
 * пометкой во второй линии. Порядок строк — как отдал сервер (по дате
 * документа, новые сверху).
 */

import { DocumentBasisLink, DocumentNumber, DocumentStatusPill, DocumentTitle, LegalProfileMark } from "./document-cells";
import { documentBasisText, documentCounterparty, documentDate, documentTitle, formatDocMoney } from "./document-view";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { DocumentDto } from "../../../lib/api-crm";

export interface DocumentColumnsContext {
  /** База адресов магазина: `/shop/<slug>`. */
  base: string;
  canOpenOrder: boolean;
  canOpenDeal: boolean;
}

/** Контрагент словами; сервер его не определил — «контрагент не указан». */
export function counterpartyText(doc: DocumentDto): string {
  return documentCounterparty(doc) ?? "контрагент не указан";
}

/** Вторая линия: контрагент и, если нужно, пометка про реквизиты. */
function secondLine(doc: DocumentDto, withKind = false) {
  // Вид — только если номер его не повторяет («УПД-2026-…» уже говорит «УПД»).
  const kind = withKind ? documentTitle(doc).kind : null;
  return (
    <>
      {kind ? `${kind} · ` : null}
      {counterpartyText(doc)}
      {doc.legalProfileIncomplete ? (
        <>
          {" · "}
          <LegalProfileMark />
        </>
      ) : null}
    </>
  );
}

function amount(doc: DocumentDto) {
  return <span className="tabular-nums whitespace-nowrap">{formatDocMoney(doc.totalGross, doc.currency)}</span>;
}

export function buildDocumentColumns(ctx: DocumentColumnsContext): ListColumn<DocumentDto>[] {
  return [
    {
      key: "document",
      header: "Документ",
      cell: (d) => <TwoLineCell primary={<DocumentTitle doc={d} />} secondary={secondLine(d)} />,
      compact: "title",
      // Компактная строка: номер целиком первой линией, вид — второй (QA-5 D7).
      compactCell: (d) => <DocumentNumber doc={d} />,
    },
    {
      key: "counterparty",
      header: "Контрагент",
      compactOnly: true,
      cell: (d) => secondLine(d),
      compact: "subtitle",
      compactCell: (d) => secondLine(d, true),
    },
    {
      // Статус в компактной строке — первым в третьей линии: в конце линии
      // пилюля уезжала за край на 360 вслед за длинным основанием.
      key: "statusCompact",
      header: "Статус",
      compactOnly: true,
      cell: (d) => <DocumentStatusPill status={d.status} />,
      compact: "meta",
    },
    {
      key: "date",
      header: "Дата",
      width: "w-[112px]",
      cell: (d) => <span className="tabular-nums">{documentDate(d)}</span>,
      compact: "meta",
      // 1024–1279: дата — второй линией под статусом, иначе колонке
      // «Документ» оставалось ~90px и номер обрезался до «УПД-202…».
      fold: { into: "status" },
    },
    {
      key: "basis",
      header: "Сделка / заказ",
      width: "w-[188px]",
      cell: (d) => <DocumentBasisLink doc={d} base={ctx.base} canOpenOrder={ctx.canOpenOrder} canOpenDeal={ctx.canOpenDeal} />,
      compact: "meta",
      compactCell: (d) => documentBasisText(d),
    },
    {
      key: "amount",
      header: "Сумма",
      width: "w-[148px]",
      align: "end",
      cell: amount,
      compact: "trailing",
      compactCell: (d) => <span className="type-body font-semibold tabular-nums whitespace-nowrap">{formatDocMoney(d.totalGross, d.currency)}</span>,
    },
    {
      key: "status",
      header: "Статус",
      width: "w-[136px]",
      cell: (d) => <DocumentStatusPill status={d.status} />,
      compact: "hidden",
    },
  ];
}
